import { notification } from 'antd';
import { createContext, useContext, useRef, useState, type MutableRefObject, type ReactElement } from 'react';
import SocketClient from 'services/socket/SocketClient';
import type { DefaultSocketConnectionErrorResponseType } from '../services/socket/SocketEvents';
import { SocketClientDefaultEvents, SocketConnectionErrorTypes, SocketConnectionEvents } from '../services/socket/SocketEvents';
import { openNotification } from 'components/notification';
import { useTranslation } from 'react-i18next';
import { refreshTokenFlow } from '../services/backgroundrefreshtoken/useBackgroundRefreshToken';

type SocketProviderType = {
  children: ReactElement;
  contentId: string;
};

type SocketContextType = {
  socketClient: SocketClient;
  connect: () => void;
  isConnected: boolean;
  isReconnecting: MutableRefObject<boolean>;
  hasPendingRequests: () => boolean;
};
const SocketContext = createContext<SocketContextType | undefined>(undefined);

export function useSocketClient(): SocketContextType {
  const socketContext = useContext(SocketContext);
  if (socketContext === undefined) {
    throw new Error('useSocketClient must be used within a SocketProvider');
  }
  return { ...socketContext };
}

export function SocketProvider({ children, contentId }: SocketProviderType) {
  const { t } = useTranslation();

  const documentEndpoint = globalThis.env.VITE_DOCUMENT_SERVICE_SOCKET_ENDPOINT as string;

  const isConnectionNotificationVisible = useRef(false);
  const isReconnecting = useRef(false);

  const refSocketClient = useRef<SocketClient>(new SocketClient(documentEndpoint, { contentId }));

  const [isConnected, setIsConnected] = useState(refSocketClient.current.getIsConnected());

  const reconnect = () => {
    isReconnecting.current = true;
    refreshTokenFlow()
      .then(() => {
        refSocketClient.current.reconnect();
      })
      .catch(() => {
        // refreshTokenFlow calls logoutUser() on failure — nothing to do here
      });
  };

  const hasSubscribedToConnectionNotifications = useRef(false);
  const connectionNotificationKey = 'socket-connection-warning';

  const showConnectionNotification = () => {
    if (isConnectionNotificationVisible.current) {
      return;
    }

    openNotification({
      key: connectionNotificationKey,
      type: 'warning',
      title: t('document.socket.connection.warning.title'),
      description: t('document.socket.connection.warning.description'),
      duration: 0,
      placement: 'bottomRight',
      action: {
        label: t('document.socket.connection.warning.reload', { defaultValue: 'Reload page' }),
        onClick: () => window.location.reload(),
        testId: 'document-socket-connection-reload',
      },
    });

    isConnectionNotificationVisible.current = true;
  };

  const hideConnectionNotification = () => {
    if (!isConnectionNotificationVisible.current) {
      return;
    }

    notification.close(connectionNotificationKey);
    isConnectionNotificationVisible.current = false;
  };

  const subscribeToDefaultErrorHandlingEvents = () => {
    const socketClient = refSocketClient.current;
    socketClient.subscribeOnce(SocketClientDefaultEvents.CONNECTION_ERROR, function ({ data }: DefaultSocketConnectionErrorResponseType) {
      if (data && data.type === SocketConnectionErrorTypes.TOKEN_EXPIRED) {
        reconnect();
      } else {
        if (!hasSubscribedToConnectionNotifications.current) {
          showConnectionNotification();
        }
        hasSubscribedToConnectionNotifications.current = true;
      }
    });

    socketClient.subscribe(SocketConnectionEvents.TOKEN_EXPIRED, () => reconnect());
  };

  const connectedCallback = () => {
    const socketClient = refSocketClient.current;
    setIsConnected(socketClient.getIsConnected());
    if (socketClient.getIsConnected()) {
      isReconnecting.current = false;
    }
    hideConnectionNotification();
  };

  const hasPendingRequests = () => refSocketClient.current.hasPendingRequests();

  const connect = () => {
    const socketClient = refSocketClient.current;
    subscribeToDefaultErrorHandlingEvents();
    socketClient.connect(connectedCallback);
  };

  return (
    <SocketContext.Provider value={{ socketClient: refSocketClient.current, connect, isConnected, isReconnecting, hasPendingRequests }}>
      {children}
    </SocketContext.Provider>
  );
}
